import { useEffect, useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { api } from '../api/client.js';
import { useAuth } from '../auth/AuthContext.jsx';

/**
 * Passwort vergessen — Anforderung.
 *
 * Antwortet bewusst immer gleich, egal ob die Adresse bekannt ist. Sonst
 * liesse sich über das Formular herausfinden, wer hier ein Konto hat.
 */
export function PasswordForgotPage() {
  const [email, setEmail] = useState('');
  const [gesendet, setGesendet] = useState(false);
  const [fehler, setFehler] = useState(null);
  const [busy, setBusy] = useState(false);

  const absenden = async () => {
    setBusy(true);
    setFehler(null);
    try {
      await api.auth.requestReset(email);
      setGesendet(true);
    } catch (e) {
      setFehler(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="panel" style={{ maxWidth: 440, margin: '32px auto' }}>
      <h2 style={{ marginBottom: 12 }}>Passwort vergessen</h2>

      {gesendet ? (
        <>
          <p>
            Falls es zu dieser Adresse ein Konto gibt, ist eine Mail mit einem
            Link unterwegs. Der Link gilt eine Stunde.
          </p>
          <p className="hinweis">
            Nichts angekommen? Schau in den Spam-Ordner. Die Zustellung ist auf
            diesem Server noch nicht vollständig eingerichtet.
          </p>
          <Link to="/konto" className="btn btn--ghost" style={{ marginTop: 8 }}>
            Zurück zur Anmeldung
          </Link>
        </>
      ) : (
        <>
          <p className="gruppe__text">
            Gib deine E-Mail-Adresse an, dann bekommst du einen Link zum Zurücksetzen.
          </p>

          <div className="field">
            <label className="field__label" htmlFor="fpmail">E-Mail</label>
            <input
              id="fpmail" className="input" type="email" value={email}
              onChange={(e) => setEmail(e.target.value)} autoComplete="email"
              onKeyDown={(e) => e.key === 'Enter' && email && absenden()}
            />
          </div>

          {fehler && <div className="error" style={{ marginBottom: 16 }}>{fehler}</div>}

          <button className="btn btn--primary" onClick={absenden} disabled={busy || !email}>
            {busy ? 'Moment…' : 'Link anfordern'}
          </button>
        </>
      )}
    </div>
  );
}

/**
 * Neues Passwort setzen — mit dem Token aus der Mail.
 */
export function PasswordResetPage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const navigate = useNavigate();
  const { neuLaden } = useAuth();

  const [passwort, setPasswort] = useState('');
  const [wiederholung, setWiederholung] = useState('');
  const [fehler, setFehler] = useState(null);
  const [busy, setBusy] = useState(false);

  const stimmtUeberein = passwort !== '' && passwort === wiederholung;

  const absenden = async () => {
    setBusy(true);
    setFehler(null);
    try {
      await api.auth.setPassword(token, passwort);
      await neuLaden();
      navigate('/mein-bereich', { replace: true });
    } catch (e) {
      setFehler(e.message);
    } finally {
      setBusy(false);
    }
  };

  if (!token) {
    return (
      <div className="panel" style={{ maxWidth: 440, margin: '32px auto' }}>
        <div className="error">Es fehlt der Link-Bestandteil aus der Mail.</div>
        <Link to="/passwort-vergessen" className="btn btn--ghost" style={{ marginTop: 16 }}>
          Neuen Link anfordern
        </Link>
      </div>
    );
  }

  return (
    <div className="panel" style={{ maxWidth: 440, margin: '32px auto' }}>
      <h2 style={{ marginBottom: 12 }}>Neues Passwort setzen</h2>

      <div className="field">
        <label className="field__label" htmlFor="pw1">Neues Passwort</label>
        <input
          id="pw1" className="input" type="password" value={passwort}
          onChange={(e) => setPasswort(e.target.value)} autoComplete="new-password"
        />
        <span className="text-faint" style={{ fontSize: '.8rem' }}>Mindestens 10 Zeichen.</span>
      </div>

      <div className="field">
        <label className="field__label" htmlFor="pw2">Wiederholen</label>
        <input
          id="pw2" className="input" type="password" value={wiederholung}
          onChange={(e) => setWiederholung(e.target.value)} autoComplete="new-password"
          onKeyDown={(e) => e.key === 'Enter' && stimmtUeberein && absenden()}
        />
        {wiederholung !== '' && !stimmtUeberein && (
          <span style={{ fontSize: '.8rem', color: 'var(--danger)' }}>
            Die beiden Eingaben stimmen nicht überein.
          </span>
        )}
      </div>

      {fehler && <div className="error" style={{ marginBottom: 16 }}>{fehler}</div>}

      <button className="btn btn--primary" onClick={absenden} disabled={busy || !stimmtUeberein}>
        {busy ? 'Moment…' : 'Passwort speichern'}
      </button>
    </div>
  );
}

/**
 * E-Mail bestätigen — läuft ohne Zutun ab, sobald die Seite geöffnet wird.
 */
export function VerifyPage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const { neuLaden } = useAuth();
  const [zustand, setZustand] = useState('laeuft');
  const [fehler, setFehler] = useState(null);

  useEffect(() => {
    if (!token) {
      setZustand('fehler');
      setFehler('Es fehlt der Link-Bestandteil aus der Mail.');
      return;
    }
    api.auth.verify(token)
      .then(async () => { await neuLaden(); setZustand('ok'); })
      .catch((e) => { setFehler(e.message); setZustand('fehler'); });
  }, [token]);

  return (
    <div className="panel" style={{ maxWidth: 440, margin: '32px auto' }}>
      <h2 style={{ marginBottom: 12 }}>E-Mail bestätigen</h2>

      {zustand === 'laeuft' && <p>Einen Moment…</p>}

      {zustand === 'ok' && (
        <>
          <p>Erledigt — deine Adresse ist bestätigt.</p>
          <Link to="/mein-bereich" className="btn btn--primary" style={{ marginTop: 16 }}>
            Zu deinem Bereich
          </Link>
        </>
      )}

      {zustand === 'fehler' && (
        <>
          <div className="error">{fehler}</div>
          <p className="hinweis">
            Bestätigungslinks gelten 24 Stunden. Ohne Bestätigung kannst du die
            Seite weiter nutzen, nur das Zurücksetzen des Passworts wäre dann
            nicht möglich.
          </p>
        </>
      )}
    </div>
  );
}
